/**
 * AdminExportsCard — super-admin data export center.
 *
 * Five exportable datasets:
 *   - leaderboard         (all users ranked by realized PnL)
 *   - trades              (every closed trade record)
 *   - obondo-chats        (assistant conversation log)
 *   - pnl-users           (per-user financial summary)
 *   - pnl-institutions    (agency-aggregated PnL)
 *
 * Preview table (first 25 rows) + one-click CSV download for each.
 */
import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Download, Table2, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useJargon } from "@/context/JargonContext";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

const KINDS = [
    { id: "leaderboard",      title: "Leaderboard",       desc: "All non-admin users ranked by realized PnL, with tier, equity, and win rate.",
      cols: ["rank", "email", "name", "tier", "equity_kes", "realized_pnl_kes", "total_trades", "win_rate_pct"] },
    { id: "trades",           title: "All trades",        desc: "Every closed trade recorded in the platform (symbol, side, qty, entry/exit, PnL).",
      cols: ["ts_closed", "email", "symbol", "side", "qty", "entry_price", "exit_price", "pnl_kes"] },
    { id: "obondo-chats",     title: "Obondo chats",      desc: "Assistant conversation log — user + Obondo messages across every session.",
      cols: ["ts", "email", "role", "content_preview"] },
    { id: "pnl-users",        title: "PnL per user",      desc: "Individual financial summary — realized + unrealized PnL, trade counts, win rate.",
      cols: ["email", "name", "tier", "equity_kes", "realized_pnl_kes", "unrealized_pnl_kes", "total_trades", "wins", "losses", "win_rate_pct"] },
    { id: "pnl-institutions", title: "PnL per institution", desc: "Aggregated PnL by agency (CMA + MoE) with seats used and best-performing trader.",
      cols: ["agency_name", "institution_type", "plan_type", "seats_used", "total_realized_pnl_kes", "total_equity_kes", "best_trader_email", "best_trader_pnl_kes"] },
];

export default function AdminExportsCard() {
    const { level } = useJargon();
    const [kind, setKind] = useState("leaderboard");
    const [rows, setRows] = useState([]);
    const [busy, setBusy] = useState(false);
    const meta = KINDS.find((k) => k.id === kind);

    async function loadPreview(k) {
        setKind(k);
        setBusy(true);
        setRows([]);
        try {
            const { data } = await api.get(`/admin/ops/exports/preview?kind=${k}&limit=25`);
            setRows(data.rows || []);
        } catch (e) {
            toast.error(`Preview failed: ${e.response?.data?.detail || e.message}`);
        } finally { setBusy(false); }
    }

    function downloadCsv(k) {
        const url = `${API_BASE}/api/admin/ops/exports/${k}.csv`;
        window.open(url, "_blank");
    }

    function printPreview() {
        window.print();
    }

    return (
        <div className="space-y-4" data-testid="admin-exports-card">
            {/* Kind picker + description */}
            <div className="grid md:grid-cols-5 gap-2">
                {KINDS.map((k) => (
                    <button
                        key={k.id}
                        onClick={() => loadPreview(k.id)}
                        data-testid={`export-kind-${k.id}`}
                        className={`text-left border rounded p-3 transition ${
                            kind === k.id
                                ? "border-primary bg-primary/10"
                                : "border-border bg-card hover:border-primary/50"
                        }`}
                    >
                        <div className="text-sm font-extrabold">
                            {level === "basic" ? k.title.replace(/PnL/gi, "Profit/Loss") : k.title}
                        </div>
                        <div className="text-[10px] text-muted-foreground mt-1 leading-tight">
                            {level === "basic" ? k.desc.replace(/PnL/gi, "profit/loss") : k.desc}
                        </div>
                    </button>
                ))}
            </div>

            {/* Actions */}
            <div className="flex gap-2 items-center flex-wrap">
                <Button size="sm" variant="outline" onClick={() => loadPreview(kind)} disabled={busy} data-testid="export-preview-btn">
                    <Table2 className="w-3.5 h-3.5 mr-1" />
                    {busy ? "Loading…" : "Load preview (25 rows)"}
                </Button>
                <Button size="sm" onClick={() => downloadCsv(kind)} data-testid="export-download-csv">
                    <Download className="w-3.5 h-3.5 mr-1" /> Download full CSV
                </Button>
                {rows.length > 0 && (
                    <Button size="sm" variant="outline" onClick={printPreview} data-testid="export-print">
                        <Printer className="w-3.5 h-3.5 mr-1" /> Print preview
                    </Button>
                )}
            </div>

            {/* Preview table */}
            {rows.length > 0 && meta && (
                <div className="border border-border bg-card rounded overflow-x-auto print:overflow-visible">
                    <table className="w-full text-xs">
                        <thead>
                            <tr className="border-b border-border text-[11px] uppercase text-muted-foreground">
                                {meta.cols.map((c) => (
                                    <th key={c} className="text-left p-2">{c}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody data-testid="export-preview-body">
                            {rows.map((r, i) => (
                                <tr key={r.id || r.ts || `export-row-${i}`} className="border-b border-border/50 hover:bg-secondary/40">
                                    {meta.cols.map((c) => (
                                        <td key={c} className="p-2 mono truncate max-w-[240px]" title={String(r[c] ?? "")}>
                                            {formatCell(r[c])}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {rows.length === 0 && !busy && (
                <div className="text-xs text-muted-foreground text-center py-6 border border-dashed border-border rounded">
                    Click "Load preview" to see the first 25 rows here — or "Download full CSV" for everything.
                </div>
            )}
        </div>
    );
}

function formatCell(v) {
    if (v === null || v === undefined || v === "") return "—";
    if (typeof v === "number") return v.toLocaleString();
    return String(v);
}
